import { AlertTriangle, MapPin } from "lucide-react";

export const LAT_Q = "392862661";
export const LNG_Q = "1531033355";
const WARN_MILES = 0.5;

const toNum = (v) => {
  const s = String(v ?? "").trim();
  // Strict decimal degrees only — "34.05 N" or "34°03'" must not pass as valid.
  return /^[-+]?\d+(\.\d+)?$/.test(s) ? parseFloat(s) : null;
};

const miles = (a, b) => {
  const R = 3958.8;
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Compare typed live coordinates with the project's site location to catch typos.
export function coordCheck(answers, project) {
  const latRaw = answers[LAT_Q]?.value;
  const lngRaw = answers[LNG_Q]?.value;
  if (!String(latRaw ?? "").trim() || !String(lngRaw ?? "").trim()) return null;
  const lat = toNum(latRaw);
  const lng = toNum(lngRaw);
  if (lat === null || lng === null) return { msg: "Coordinates must be numbers in decimal degrees (e.g. 34.052235, -118.243683)." };
  const site = { lat: toNum(project.latitude), lng: toNum(project.longitude) };
  const hasSite = site.lat !== null && site.lng !== null;
  // Check for swapped fields first — a western-US swap puts -118 in latitude and fails the range check.
  if (hasSite && miles({ lat: lng, lng: lat }, site) <= WARN_MILES) {
    return { msg: "Latitude and longitude look swapped — latitude goes in the first box. Double-check the MyGPSCoordinates reading.", far: true };
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return { msg: "Latitude must be between -90 and 90 and longitude between -180 and 180." };
  if (!hasSite) return null;
  const d = miles({ lat, lng }, site);
  if (d <= WARN_MILES) return { ok: true, msg: `${d < 0.1 ? Math.round(d * 5280) + " ft" : d.toFixed(2) + " mi"} from the project's site location.` };
  let hint = "";
  if (miles({ lat, lng: -lng }, site) <= WARN_MILES) hint = " The longitude looks like it's missing a minus sign.";
  return { msg: `These coordinates are ${d.toFixed(1)} miles from the project's site location (${site.lat}, ${site.lng}).${hint} Double-check the MyGPSCoordinates reading before sending to Pano.`, far: true };
}

export default function CoordWarning({ answers, project }) {
  const c = coordCheck(answers, project);
  if (!c) return null;
  if (c.ok) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-emerald-400" data-testid="pano-coord-ok">
        <MapPin className="w-3.5 h-3.5" /> {c.msg}
      </div>
    );
  }
  return (
    <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-700 bg-amber-950/40 p-2.5 text-xs text-amber-100" data-testid="pano-coord-warning">
      <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> {c.msg}
    </div>
  );
}
